import { Gender } from '@/types/dashboard.types'

interface ChildAvatarProps {
  gender: Gender
  size?: 'sm' | 'md' | 'lg' | 'xl'
  className?: string
}

const sizeMap = {
  sm: 'w-12 h-12',
  md: 'w-16 h-16',
  lg: 'w-24 h-24',
  xl: 'w-32 h-32',
}

export const ChildAvatar = ({ gender, size = 'md', className = '' }: ChildAvatarProps) => {
  const sizeClass = sizeMap[size]

  if (gender === Gender.MALE) {
    return (
      <svg
        className={`${sizeClass} ${className}`}
        viewBox="0 0 100 100"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        {/* Background */}
        <circle cx="50" cy="50" r="48" fill="#E0F2FE" />
        
        {/* Face */}
        <circle cx="50" cy="45" r="20" fill="#FCD5B4" />
        
        {/* Hair */}
        <path
          d="M50 25C40 25 32 30 30 38C30 35 32 30 40 28C45 26 55 26 60 28C68 30 70 35 70 38C68 30 60 25 50 25Z"
          fill="#3B2314"
        />
        
        {/* Eyes */}
        <circle cx="43" cy="43" r="2" fill="#2C1810" />
        <circle cx="57" cy="43" r="2" fill="#2C1810" />
        
        {/* Smile */}
        <path
          d="M43 50C45 52 47 53 50 53C53 53 55 52 57 50"
          stroke="#2C1810"
          strokeWidth="2"
          strokeLinecap="round"
          fill="none"
        />
        
        {/* Body */}
        <path
          d="M35 65C35 62 40 60 50 60C60 60 65 62 65 65V80C65 85 60 88 50 88C40 88 35 85 35 80V65Z"
          fill="#60A5FA"
        />
      </svg>
    )
  }

  // Female avatar
  return (
    <svg
      className={`${sizeClass} ${className}`}
      viewBox="0 0 100 100"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Background */}
      <circle cx="50" cy="50" r="48" fill="#FCE7F3" />
      
      {/* Face */}
      <circle cx="50" cy="45" r="20" fill="#FCD5B4" />
      
      {/* Hair - longer style */}
      <path
        d="M50 25C40 25 32 30 30 38C30 35 28 30 30 40C30 45 32 50 35 52L38 60C40 58 35 50 35 45C35 42 38 38 45 35C48 33 52 33 55 35C62 38 65 42 65 45C65 50 60 58 62 60L65 52C68 50 70 45 70 40C72 30 70 35 70 38C68 30 60 25 50 25Z"
        fill="#3B2314"
      />
      
      {/* Hair bow */}
      <circle cx="38" cy="32" r="4" fill="#EC4899" />
      <circle cx="62" cy="32" r="4" fill="#EC4899" />
      
      {/* Eyes */}
      <circle cx="43" cy="43" r="2" fill="#2C1810" />
      <circle cx="57" cy="43" r="2" fill="#2C1810" />
      
      {/* Smile */}
      <path
        d="M43 50C45 52 47 53 50 53C53 53 55 52 57 50"
        stroke="#2C1810"
        strokeWidth="2"
        strokeLinecap="round"
        fill="none"
      />
      
      {/* Body */}
      <path
        d="M35 65C35 62 40 60 50 60C60 60 65 62 65 65V80C65 85 60 88 50 88C40 88 35 85 35 80V65Z"
        fill="#F472B6"
      />
    </svg>
  )
}

export const ChildIllustration = ({ className = '' }: { className?: string }) => {
  return (
    <svg
      className={className}
      viewBox="0 0 400 300"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Background elements */}
      <circle cx="80" cy="60" r="30" fill="#FEF3C7" opacity="0.5" />
      <circle cx="320" cy="80" r="40" fill="#DBEAFE" opacity="0.5" />
      <circle cx="350" cy="200" r="35" fill="#FCE7F3" opacity="0.5" />
      
      {/* Three children illustration */}
      {/* Child 1 - Left */}
      <g transform="translate(60, 100)">
        <circle cx="0" cy="0" r="25" fill="#FCD5B4" />
        <path d="M-15 -20C-10 -25 10 -25 15 -20C15 -15 10 -10 0 -10C-10 -10 -15 -15 -15 -20Z" fill="#3B2314" />
        <circle cx="-8" cy="-2" r="2.5" fill="#2C1810" />
        <circle cx="8" cy="-2" r="2.5" fill="#2C1810" />
        <path d="M-6 8C-2 11 2 11 6 8" stroke="#2C1810" strokeWidth="2" strokeLinecap="round" />
        <rect x="-20" y="25" width="40" height="50" rx="8" fill="#60A5FA" />
        <rect x="-20" y="75" width="18" height="35" rx="4" fill="#FCD5B4" />
        <rect x="2" y="75" width="18" height="35" rx="4" fill="#FCD5B4" />
      </g>
      
      {/* Child 2 - Center */}
      <g transform="translate(200, 90)">
        <circle cx="0" cy="0" r="28" fill="#FCD5B4" />
        <path d="M-18 -22C-12 -28 12 -28 18 -22C18 -16 12 -10 0 -10C-12 -10 -18 -16 -18 -22Z" fill="#3B2314" />
        <circle cx="-5" cy="28" r="5" fill="#EC4899" />
        <circle cx="15" cy="28" r="5" fill="#EC4899" />
        <circle cx="-9" cy="-2" r="3" fill="#2C1810" />
        <circle cx="9" cy="-2" r="3" fill="#2C1810" />
        <path d="M-7 10C-3 13 3 13 7 10" stroke="#2C1810" strokeWidth="2" strokeLinecap="round" />
        <rect x="-22" y="28" width="44" height="55" rx="10" fill="#F472B6" />
        <rect x="-22" y="83" width="20" height="38" rx="5" fill="#FCD5B4" />
        <rect x="2" y="83" width="20" height="38" rx="5" fill="#FCD5B4" />
      </g>
      
      {/* Child 3 - Right */}
      <g transform="translate(340, 105)">
        <circle cx="0" cy="0" r="24" fill="#FCD5B4" />
        <path d="M-14 -18C-9 -23 9 -23 14 -18C14 -13 9 -8 0 -8C-9 -8 -14 -13 -14 -18Z" fill="#3B2314" />
        <circle cx="-7" cy="-1" r="2.5" fill="#2C1810" />
        <circle cx="7" cy="-1" r="2.5" fill="#2C1810" />
        <path d="M-5 7C-2 10 2 10 5 7" stroke="#2C1810" strokeWidth="2" strokeLinecap="round" />
        <rect x="-19" y="24" width="38" height="48" rx="8" fill="#A78BFA" />
        <rect x="-19" y="72" width="17" height="33" rx="4" fill="#FCD5B4" />
        <rect x="2" y="72" width="17" height="33" rx="4" fill="#FCD5B4" />
      </g>
      
      {/* Decorative elements */}
      <path d="M 100 220 Q 200 200 300 220" stroke="#7DD3C0" strokeWidth="4" fill="none" opacity="0.3" />
    </svg>
  )
}
